import { MaybeRef, useNow } from '@vueuse/core';
import { ref, isRef, unref, watch, watchEffect } from 'vue';
import { useAptosClient } from '@/composables/useAptosClient';
import { composeType } from '@/utils/contracts';
import { moduleAddress } from '@/utils/networkData';

const OFFSET = 12 * 60 * 60 * 1000; // 12 hours

export function useFaucetTokenRestriction(
  address: MaybeRef<string | undefined>,
  token: MaybeRef<string | undefined>,
) {
  const loading = ref<boolean>(true);
  const { client } = useAptosClient();
  const now = useNow({ interval: 1000 });
  const cooldown = ref(0);

  const status = ref({
    since: 0,
    status: false,
  });

  if (isRef(address) || isRef(token)) {
    watchEffect(refetch);
  } else {
    refetch();
  }

  async function refetch() {
    loading.value = true;

    const _address = unref(address);
    const _token = unref(token);

    const result = {
      since: 0,
      status: false,
    };

    if (_address && _token) {
      const resource = await client.getAccountResource(
        _address,
        composeType(moduleAddress('Faucet'), 'Restricted', [_token]),
      );
      if (!resource) {
        result.status = true;
      } else {
        result.since = +(resource.data.since * 1000);
        result.status = result.since + OFFSET < Date.now();
      }
    }

    status.value = result;
    loading.value = false;
  }

  watch([status, now], () => {
    if (status.value.since > 0) {
      cooldown.value = status.value.since + OFFSET - +now.value;
    }
    if (cooldown.value < 0) {
      cooldown.value = 0;
      status.value.since = 0;
      loading.value = false;
      status.value.status = true;
    }
  });

  return {
    refetch,
    status,
    loading,
    cooldown,
  };
}
